import React from 'react';
import PropTypes from 'prop-types';
import FlexLib from '@flex/lib';
import { TextField } from '@mui/material';
const { formatCurrency, formatCurrencyInput } = FlexLib.utils.feeService;

const boldStyle = {
  WebkitTextFillColor: '#000000de',
  'font-weight': 'bold',
};

// Note: can set precision, but the display precision will always be 2 for now.
const AmountTextField = ({
  id,
  label = 'Amount',
  amount,
  setAmount,
  precision = 2,
  disabled = false,
  required = false,
  variant = 'outlined',
  fullWidth = false,
  error = false,
  isBold = false,
}) => {
  const [displayAmount, setDisplayAmount] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [validAmountRegex, setValidAmountRegex] = React.useState(new RegExp('^\\d*\\.?\\d{0,' + precision + '}$'));
  const [style, setStyle] = React.useState({});

  React.useEffect(() => {
    // needed to handle changing amount from outside
    // if component is in focus or if the amount is empty, display working amount
    // otherwise show currency formatted amount
    setDisplayAmount(isFocused || !amount ? amount : formatCurrency(amount));
  }, [amount]);

  React.useEffect(() => {
    setValidAmountRegex(new RegExp('^\\d*\\.?\\d{0,' + precision + '}$'));
  }, [precision]);

  React.useEffect(() => {
    let tempStyle = {};
    if (disabled) {
      tempStyle = { ...tempStyle, '& .Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.49)' } };
    }

    if (isBold) {
      tempStyle = { ...tempStyle, ...boldStyle, '& .MuiInputBase-input': boldStyle, '& .Mui-disabled': boldStyle };
    }

    setStyle(tempStyle);
  }, [disabled, isBold]);

  const handleAmountChange = (event) => {
    event.preventDefault();
    const value = event.target.value;
    // trim first before testing to allow pasting of display amount
    const trimmedValue = value.replace(/[$,\-A-Za-z]/g, '');
    if (!value) {
      // cleared the whole string
      setAmount('');
    } else if (validAmountRegex.test(trimmedValue)) {
      // don't enter values that aren't valid
      setAmount(trimmedValue);
    }
    // otherwise character is not allowed
  };

  const handleAmountBlur = () => {
    // show display amount
    setIsFocused(false);
    const cleanedAmount = formatCurrencyInput(amount);
    setAmount(cleanedAmount);
    setDisplayAmount(cleanedAmount ? formatCurrency(cleanedAmount) : '');
  };

  const handleAmountFocus = () => {
    // show working amount (no format)
    setIsFocused(true);
    setDisplayAmount(amount);
  };
  return (
    <TextField
      disabled={disabled}
      required={required}
      variant={variant}
      data-cy={id}
      id={id}
      fullWidth={fullWidth}
      label={label}
      value={displayAmount}
      onChange={handleAmountChange}
      onBlur={handleAmountBlur}
      onFocus={handleAmountFocus}
      error={error}
      sx={style}
    />
  );
};

AmountTextField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  amount: PropTypes.string,
  setAmount: PropTypes.func,
  precision: PropTypes.number,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  isBold: PropTypes.bool,
};

export default AmountTextField;
